import React, { useMemo, useState } from 'react';
import { Modal, Form, Select, InputNumber, Row, Col, Typography, Button, Tag, Table, Input, message } from 'antd';
import { SwapOutlined, ArrowRightOutlined, SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import { useGetStockBodega } from '../../../hooks/useBodegas';

const { Text } = Typography;

const ModalTraslado = ({ isOpen, onClose, bodegas, productos, cargandoProductos, onTrasladar, isPending }) => {
    const [form] = Form.useForm();
    
    const bodegaOrigenId = Form.useWatch('bodega_origen_id', form);
    const [listaTraslado, setListaTraslado] = useState([]);

    // Traemos el stock en tiempo real de la bodega de origen seleccionada
    const { data: stockOrigen, isFetching: cargandoStockOrigen } = useGetStockBodega(bodegaOrigenId);

    // Pre-procesar opciones filtrando lo que realmente se puede mover (Stock > 0)
    const opcionesProductos = useMemo(() => {
        const opciones = [];
        productos.forEach(p => {
            if (p.es_combo) {
                // Los combos siempre pasan porque su stock se arma dinámicamente en la BD (despiece)
                opciones.push({
                    value: `prod_${p.id}`,
                    nombre_plano: p.nombre,
                    producto_id: p.id,
                    variante_id: null,
                    es_combo: true,
                    search_str: `${p.nombre} ${p.codigo_barras || ''} ${p.codigo_interno || ''}`,
                    stock_disponible: 'combo' 
                });
            } else if (p.has_variants && p.variantes?.length > 0) {
                p.variantes.forEach(v => {
                    // Buscar el stock físico de esta variante en la bodega origen
                    const registro = stockOrigen?.find(sb => sb.producto_id === p.id && sb.variante_id === v.id);
                    const qty = registro ? Number(registro.stock) : 0;
                    
                    // REGLA DE NEGOCIO: Solo mostrar si hay stock físico para trasladar
                    if (qty > 0) {
                        const nombreVar = `${p.nombre} (${v.atributos?.nombre || 'Var'})`;
                        opciones.push({
                            value: `var_${v.id}`,
                            nombre_plano: nombreVar,
                            producto_id: p.id,
                            variante_id: v.id,
                            es_combo: false,
                            search_str: `${nombreVar} ${v.codigo_barras || p.codigo_barras || ''} ${v.codigo_interno || p.codigo_interno || ''}`,
                            stock_disponible: qty
                        });
                    }
                });
            } else {
                // Buscar el stock físico de este producto normal en la bodega origen
                const registro = stockOrigen?.find(sb => sb.producto_id === p.id && !sb.variante_id);
                const qty = registro ? Number(registro.stock) : 0;
                
                // REGLA DE NEGOCIO: Solo mostrar si hay stock físico para trasladar
                if (qty > 0) {
                    opciones.push({
                        value: `prod_${p.id}`,
                        nombre_plano: p.nombre,
                        producto_id: p.id,
                        variante_id: null,
                        es_combo: false,
                        search_str: `${p.nombre} ${p.codigo_barras || ''} ${p.codigo_interno || ''}`,
                        stock_disponible: qty
                    });
                }
            }
        });
        return opciones;
    }, [productos, stockOrigen]);

    // Lógica del buscador Omnibox
    const handleSeleccionarProducto = (val, option) => {
        if (!option) return;
        
        // Bloqueo de seguridad: No se puede pistolear sin elegir origen
        if (!bodegaOrigenId) {
            return message.warning("Primero selecciona la Bodega de Origen para validar el stock.");
        }

        const { value: uniqueId, nombre_plano, producto_id, variante_id, es_combo, stock_disponible } = option;

        setListaTraslado(prev => {
            const index = prev.findIndex(i => i.value === uniqueId);
            if (index >= 0) {
                const nuevos = [...prev];
                // Evitamos que agregue más de lo que hay en bodega
                if (!es_combo && nuevos[index].cantidad + 1 > stock_disponible) {
                    message.warning(`Solo hay ${stock_disponible} unidades disponibles de ${nombre_plano}.`);
                    return nuevos;
                }
                nuevos[index].cantidad += 1;
                return nuevos;
            } else {
                return [...prev, {
                    key: uniqueId,
                    value: uniqueId,
                    label: nombre_plano,
                    producto_id,
                    variante_id,
                    cantidad: 1,
                    es_combo,
                    stock_disponible
                }];
            }
        });
    };

    const actualizarCantidad = (id, nuevaCant) => {
        setListaTraslado(prev => prev.map(item => {
            if (item.value !== id) return item;
            return { ...item, cantidad: nuevaCant || 1 };
        }));
    };

    const eliminarItem = (id) => setListaTraslado(listaTraslado.filter(i => i.value !== id));

    const handleEjecutarLote = () => {
        if (listaTraslado.length === 0) return message.warning("Agrega al menos un producto a la lista.");
        
        // Ejecuta la validación de los campos obligatorios del Formulario (incluye Observaciones)
        form.validateFields().then(values => {
            onTrasladar({ 
                bodega_origen_id: values.bodega_origen_id, 
                bodega_destino_id: values.bodega_destino_id, 
                observaciones: values.observaciones, 
                items: listaTraslado 
            });
        }).catch(info => {
            message.warning("Completa los campos obligatorios antes de continuar.");
        });
    };

    const cerrarYLimpiar = () => {
        form.resetFields();
        setListaTraslado([]);
        onClose();
    };

    const columnasCarrito = [
        { 
            title: 'Producto', 
            dataIndex: 'label', 
            render: (t, r) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Text strong style={{fontSize: '13px'}}>{t} {r.es_combo && <Tag color="purple" style={{fontSize: '10px', marginLeft: 4}}>Combo</Tag>}</Text>
                    <Text type="secondary" style={{ fontSize: '11px' }}>
                        Stock en bodega: {r.es_combo ? 'Dinámico' : <Tag color='default' style={{ margin: 0, padding: '0 4px', fontSize: '10px' }}>{r.stock_disponible} unds</Tag>}
                    </Text>
                </div>
            ) 
        },
        { 
            title: 'Cant.', 
            dataIndex: 'cantidad', 
            align: 'center', 
            width: 100, 
            render: (v, r) => (
                <InputNumber 
                    min={1} 
                    max={r.es_combo ? undefined : r.stock_disponible} 
                    value={v} 
                    size="middle" 
                    style={{ width: '100%' }} 
                    controls={false} // SIN FLECHAS
                    onChange={(val) => actualizarCantidad(r.value, val)}
                    onFocus={(e) => e.target.select()}
                />
            ) 
        },
        { 
            title: '', 
            key: 'action', 
            width: 50, 
            align: 'center', 
            render: (_, record) => (
                <Button type="text" danger icon={<DeleteOutlined />} onClick={() => eliminarItem(record.value)} />
            )
        }
    ];

    return (
        <Modal title={<><SwapOutlined /> Traslado de Inventario</>} open={isOpen} onCancel={cerrarYLimpiar} footer={null} centered width={750} destroyOnClose>
            <Form layout="vertical" form={form} style={{ marginTop: '16px' }}>
                <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                    <Row gutter={16}>
                        <Col span={11}>
                            <Form.Item name="bodega_origen_id" label={<Text strong>Bodega Origen</Text>} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                <Select size="large" placeholder="Desde dónde..." options={bodegas.filter(b => b.activa).map(b => ({ label: b.nombre, value: b.id }))} 
                                    onChange={() => { setListaTraslado([]); form.setFieldsValue({ bodega_destino_id: null }); }} 
                                />
                            </Form.Item>
                        </Col>
                        <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '30px' }}>
                            <ArrowRightOutlined style={{ color: '#94a3b8', fontSize: '18px' }} />
                        </Col>
                        <Col span={11}>
                            <Form.Item name="bodega_destino_id" label={<Text strong>Bodega Destino</Text>} rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                <Select size="large" placeholder="Hacia dónde..." disabled={!bodegaOrigenId} options={bodegas.filter(b => b.activa && b.id !== bodegaOrigenId).map(b => ({ label: b.nombre, value: b.id }))} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <Select
                        showSearch
                        autoFocus
                        value={null}
                        size="large"
                        placeholder={bodegaOrigenId ? "🔍 Escanear código de barras o escribir nombre..." : "⚠️ Selecciona la Bodega de Origen primero"}
                        disabled={!bodegaOrigenId || cargandoProductos || cargandoStockOrigen}
                        suffixIcon={<SearchOutlined />}
                        style={{ width: '100%' }}
                        onChange={(val, option) => handleSeleccionarProducto(val, option)}
                        filterOption={(input, option) => (option?.search_str ?? '').toLowerCase().includes(input.toLowerCase())}
                        options={opcionesProductos.map(op => ({
                            ...op,
                            label: (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text strong>{op.nombre_plano}</Text>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {op.es_combo ? 'Combo' : `Stock: ${op.stock_disponible}`}
                                    </Text>
                                </div>
                            )
                        }))}
                    />
                </div>

                <Table 
                    dataSource={listaTraslado} 
                    columns={columnasCarrito} 
                    pagination={false} 
                    size="small" 
                    scroll={{ y: 250 }} 
                    style={{ border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '20px' }} 
                    locale={{ emptyText: 'Busca o escanea un producto para agregarlo al traslado.' }}
                />

                <Form.Item 
                    name="observaciones" 
                    label={<Text strong>Observaciones Generales</Text>} 
                    rules={[{ required: true, message: 'La observación es obligatoria (ej. Despacho Ruta Norte)', min: 3 }]}
                >
                    <Input.TextArea rows={2} placeholder="Ej: Mercancía para la ruta del centro..." />
                </Form.Item>

                <Button type="primary" onClick={handleEjecutarLote} block size="large" loading={isPending} disabled={listaTraslado.length === 0} style={{ background: '#22BABB', fontWeight: 'bold', height: '45px' }}>
                    EJECUTAR TRASLADO DE ÍTEMS
                </Button>
            </Form>
        </Modal>
    );
};

export default ModalTraslado;